import { useState } from "react";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import CircleIconButton from "app/storybookComponents/Button/CircleIconButton";
import Button from "app/storybookComponents/Button";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";
import { useAppSelector } from "utils/redux/hooks";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import RecipientSection from "./RecipientSection";
import { EmailRecipientsPreview } from "../types";
import {
  getPreviouslyEmailedLabel,
  getUserAccountIdsToExclude,
} from "./previouslyEmailedHelpers";
import {
  getAutoExcludedReasonLabel,
  getRecipientsCsvRows,
  RECIPIENTS_CSV_HEADERS,
} from "./recipientsCsvHelpers";

interface Props {
  preview: EmailRecipientsPreview | null;
  isLoading: boolean;
  hasError: boolean;
  excludedUserAccountIds: number[];
  onSave: (excludedUserAccountIds: number[]) => void;
  onCancel: () => void;
}

const formatEmailedDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const RecipientsListScreen = ({
  preview,
  isLoading,
  hasError,
  excludedUserAccountIds,
  onSave,
  onCancel,
}: Props) => {
  // Exclusions are edited against a local draft so Cancel can walk away from them. The
  // screen is unmounted whenever it is left, so the draft always restarts from whatever
  // the last Save committed.
  const [draftExcludedIds, setDraftExcludedIds] = useState<number[]>(
    excludedUserAccountIds
  );
  const companyUsers = useAppSelector(selectAllCompanyUsersById);
  const recipients = preview?.recipients ?? [];
  const autoExcluded = preview?.autoExcluded ?? [];
  const previouslyEmailed =
    preview?.eligibleRecipientsThatReceivedEmailsBefore ?? {};
  const excludedIdSet = new Set(draftExcludedIds);

  const excludeUser = (userAccountId: number) =>
    setDraftExcludedIds((prev) =>
      prev.includes(userAccountId) ? prev : [...prev, userAccountId]
    );

  const excludeManyUsers = (userAccountIds: number[]) =>
    setDraftExcludedIds((prev) => [...new Set([...prev, ...userAccountIds])]);

  const includeUser = (userAccountId: number) =>
    setDraftExcludedIds((prev) => prev.filter((id) => id !== userAccountId));

  const visibleRecipients = recipients.filter(
    (user) => !excludedIdSet.has(user.userAccountId)
  );
  const manuallyExcludedUsers = recipients.filter((user) =>
    excludedIdSet.has(user.userAccountId)
  );

  if (isLoading) {
    return (
      <>
        <p className="text-xs text-charcoal-80">Loading recipients...</p>
        <SimpleModalFooter
          onPrimaryButtonClick={onCancel}
          primaryButtonText="Close"
        />
      </>
    );
  }

  // Without this an unauthorized or failed preview reads as "no one matched",
  // which is a different thing than "we could not check".
  if (hasError) {
    return (
      <>
        <p className="text-xs text-charcoal-80">
          We couldn’t load the recipient list. Close this and try again.
        </p>
        <SimpleModalFooter
          onPrimaryButtonClick={onCancel}
          primaryButtonText="Close"
        />
      </>
    );
  }

  const idsToBulkExclude = getUserAccountIdsToExclude(previouslyEmailed).filter(
    (userAccountId) => !excludedIdSet.has(userAccountId)
  );

  // Hidden once there is nothing left to exclude - a bar reading "0 people have already
  // received this email" is noise, and it is also what a list of only partly emailed
  // recipients would show, who must not be excluded in bulk.
  const getBulkExcludeControl = () => {
    if (!idsToBulkExclude.length) {
      return null;
    }

    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg bg-charcoal-05 p-3">
        <span className="text-xs text-charcoal-80">
          {`${idsToBulkExclude.length} ${
            idsToBulkExclude.length === 1 ? "person has" : "people have"
          } already received this email.`}
        </span>
        <Button
          className="ms-auto"
          onClick={() => excludeManyUsers(idsToBulkExclude)}
        >
          {`Exclude ${idsToBulkExclude.length}`}
        </Button>
      </div>
    );
  };

  const totalRecipientCount = recipients.length + autoExcluded.length;

  return (
    <>
      <div className="flex flex-col gap-[20px]">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-charcoal-80">
            {`${totalRecipientCount} ${
              totalRecipientCount === 1 ? "person" : "people"
            } matched your selection.`}
          </p>
          <ExportCSVButton
            headers={RECIPIENTS_CSV_HEADERS}
            getRows={() =>
              getRecipientsCsvRows(preview, draftExcludedIds, {
                getEmailAddress: (userAccountId) =>
                  companyUsers[userAccountId]?.emailAddress,
                formatDate: formatEmailedDate,
              })
            }
            fileName="email-recipients"
            disabled={!totalRecipientCount}
            buttonText="Download CSV"
            variant="link"
            className="text-xs"
          />
        </div>
        {getBulkExcludeControl()}
        <RecipientSection
          title={`Recipients (${visibleRecipients.length})`}
          users={visibleRecipients}
          emptyState={
            <p className="p-2 text-xs text-charcoal-80">
              No recipients match your selection.
            </p>
          }
          renderRowAction={(user) => {
            const emailedBefore = previouslyEmailed[user.userAccountId];
            return (
              <div className="flex flex-shrink-0 items-center gap-2">
                {emailedBefore ? (
                  <span className="text-xs text-charcoal-80">
                    {getPreviouslyEmailedLabel(
                      emailedBefore,
                      formatEmailedDate
                    )}
                  </span>
                ) : null}
                <CircleIconButton
                  icon="times"
                  size="small"
                  onClick={() => excludeUser(user.userAccountId)}
                  ariaLabel={`Exclude ${user.firstName} ${user.lastName}`}
                />
              </div>
            );
          }}
        />
        {manuallyExcludedUsers.length ? (
          <RecipientSection
            title={`Manually Excluded (${manuallyExcludedUsers.length})`}
            users={manuallyExcludedUsers}
            renderRowAction={(user) => (
              <CircleIconButton
                icon="undo"
                size="small"
                onClick={() => includeUser(user.userAccountId)}
                ariaLabel={`Include ${user.firstName} ${user.lastName}`}
              />
            )}
          />
        ) : null}
        {autoExcluded.length ? (
          <RecipientSection
            title={`Automatically Excluded (${autoExcluded.length})`}
            users={autoExcluded}
            renderRowAction={(user) => (
              <span className="flex-shrink-0 text-xs text-charcoal-80">
                {getAutoExcludedReasonLabel(user.reason)}
              </span>
            )}
          />
        ) : null}
      </div>
      <SimpleModalFooter
        onBackButtonClick={onCancel}
        backButtonText="Cancel"
        onPrimaryButtonClick={() => onSave(draftExcludedIds)}
        primaryButtonText="Save"
      />
    </>
  );
};

export default RecipientsListScreen;
